import { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { toast } from 'react-toastify';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = 'Asia/Bangkok';

/** วันนี้ (เวลาไทย) ในรูป YYYY-MM-DD — ค่าเริ่มต้นของช่องวันที่ในหน้ารายงาน/ประวัติ */
export const todayBangkok = (): string => dayjs().tz(TZ).format('YYYY-MM-DD');

export interface DateRangeFilter {
  /** ค่าในช่อง input (ยังไม่กดค้นหา) */
  dateFrom: string;
  dateTo: string;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  /** ค่าที่ใช้ยิง API จริง (หลังกดค้นหา) */
  appliedDateFrom: string;
  appliedDateTo: string;
  /** ข้อความในช่องค้นหา (controlled) */
  searchInput: string;
  setSearchInput: (v: string) => void;
  /** คำค้นหลัง debounce — ใช้ยิง API; เปลี่ยนเมื่อไหร่หน้าจะกลับเป็น 1 ให้เอง */
  searchTerm: string;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  /** ตรวจ from <= to (toast ถ้าผิด) แล้วนำช่วงวันที่ไปใช้ + กลับหน้า 1 — คืน false ถ้าไม่ผ่าน */
  apply: () => boolean;
  /** กลับเป็นวันนี้/วันนี้ ล้างคำค้น หน้า 1 (ตัวกรองอื่นของหน้าให้รีเซ็ตเองต่อจากนี้) */
  reset: () => void;
  /** ตั้งช่วงวันที่ทั้ง input และ applied ตรง ๆ (ส่ง '' เพื่อไม่กรองวันที่) */
  setRange: (from: string, to: string) => void;
}

interface Options {
  debounceMs?: number;
  initialFrom?: string;
  initialTo?: string;
}

/**
 * ชุด state มาตรฐานของตัวกรอง "ช่วงวันที่ + ค้นหา (debounce) + หน้า" ที่ซ้ำกันทุกตารางประวัติ
 * ใช้คู่กับ <UiFilterPanel dateFrom.. onSearch={f.apply} onReset={f.reset}> และ <UiSearchField value={f.searchInput} onChange={f.setSearchInput}>
 */
export function useDateRangeFilter({ debounceMs = 800, initialFrom, initialTo }: Options = {}): DateRangeFilter {
  const [dateFrom, setDateFrom] = useState(() => initialFrom ?? todayBangkok());
  const [dateTo, setDateTo] = useState(() => initialTo ?? todayBangkok());
  const [appliedDateFrom, setAppliedDateFrom] = useState(dateFrom);
  const [appliedDateTo, setAppliedDateTo] = useState(dateTo);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  // debounce คำค้น + กลับหน้า 1 ในทีเดียว (กันยิง API ซ้ำด้วยหน้าเก่า)
  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(searchInput); setPage(1); }, debounceMs);
    return () => clearTimeout(t);
  }, [searchInput, debounceMs]);

  const apply = useCallback(() => {
    if (dateFrom && dateTo && dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return false;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
    return true;
  }, [dateFrom, dateTo]);

  const setRange = useCallback((from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setAppliedDateFrom(from);
    setAppliedDateTo(to);
    setPage(1);
  }, []);

  const reset = useCallback(() => {
    const today = todayBangkok();
    setRange(today, today);
    setSearchInput('');
    setSearchTerm('');
  }, [setRange]);

  return {
    dateFrom, dateTo, setDateFrom, setDateTo,
    appliedDateFrom, appliedDateTo,
    searchInput, setSearchInput, searchTerm,
    page, setPage,
    apply, reset, setRange,
  };
}
